/**
 * ReviewQueueModal — HITL approval gate for agent write tools. Drop-in
 * replacement for AgentConfirmationModal (same props, same onSubmit contract:
 * an ordered ResumeDecision[] posted to /disasterchat/resume).
 */
import React, { useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { Modal } from 'react-bootstrap';
import { StylishNewButton } from 'components/DesignSystems/New/StylishNewButton';
import { SharedIcon } from 'components/SharedIcon/SharedIcon';
import { useAors } from 'hooks/useAors';
import { useGetRostersForGroup } from 'hooks/incident/useRoster';
import { useIncidentObjectives } from 'components/IAP/hooks/incidentObjectiveHooks';
import { useResourceSets } from 'components/IAP/hooks/resourceSetHooks';
import { useIncidentRoster } from 'components/IAP/hooks/incidentRoster/useIncidentRoster';
import { INCIDENT } from 'components/IAP/ICSConstants';
import { selectGroupGuid } from 'slices/commonSelectors';
import {
  batchSignature,
  itemStatus,
  unitAllowedDecisions,
  useReviewQueue,
} from './useReviewQueue';
import BatchNavigator, { STATUS_META } from './components/BatchNavigator';
import ChangeReview from './components/ChangeReview';
import type { PendingConfirmation, ResumeDecision } from './types';
import type { SelectOption as ConfigSelectOption } from '../confirmationFieldConfig';

interface ReviewQueueModalProps {
  show: boolean;
  confirmations: PendingConfirmation[];
  askText?: string;
  submitting?: boolean;
  /**
   * Submit the resume decisions. Resolve `false` (or reject) if the resume
   * request failed, so the modal can leave the "applying" spinner and let the
   * reviewer retry instead of hanging on it.
   */
  onSubmit: (decisions: ResumeDecision[]) => void | Promise<boolean | void>;
  onClose?: () => void;
}

const hasText = (s?: string): boolean => !!s && s.trim().length > 0;

const ReviewQueueModal: React.FC<ReviewQueueModalProps> = ({
  show,
  confirmations,
  askText,
  submitting = false,
  onSubmit,
  onClose,
}) => {
  const queue = useReviewQueue(confirmations);
  const {
    state,
    currentUnit,
    currentItem,
    currentState,
    allDecided,
    decidedCount,
  } = queue;

  // Re-seed only when a new non-empty batch arrives, comparing CONTENT, not
  // array identity: every dchat poll response replaces the whole dchat object,
  // so the same batch is redelivered under a new reference (straggler polls
  // land right after the modal opens) — resetting then would wipe the user's
  // first Accept/Reject. A genuinely new batch after Submit must still reseed
  // even if byte-identical (a re-review cycle), which `awaitingNextBatch`
  // (armed in handleSubmit) covers. The parent passes a fresh `[]` on every
  // idle render, so the empty case never reseeds (cycle would churn).
  const [seeded, setSeeded] = useState(() => ({
    ref: confirmations,
    sig: batchSignature(confirmations),
  }));
  const [awaitingNextBatch, setAwaitingNextBatch] = useState(false);
  if (confirmations.length > 0 && seeded.ref !== confirmations) {
    const sig = batchSignature(confirmations);
    if (sig !== seeded.sig || awaitingNextBatch) {
      const isReReview = seeded.sig !== batchSignature([]) || awaitingNextBatch;
      setSeeded({ ref: confirmations, sig });
      setAwaitingNextBatch(false);
      queue.reset(confirmations, isReReview ? state.cycle + 1 : 1);
    } else {
      // Same content, new identity: adopt the reference, keep decisions.
      setSeeded({ ref: confirmations, sig });
    }
  }

  const { data: aors } = useAors();
  const aorOptions = useMemo<ConfigSelectOption[]>(
    () =>
      (Array.isArray(aors) ? aors : []).map(
        (a: { id: string; name: string }) => ({
          value: a.id,
          label: a.name,
        })
      ),
    [aors]
  );

  // id→name option lists so linked objective / resources / POC show by name
  // instead of a UUID. Incident-scoped hooks are empty outside an incident.
  const objectiveOptions = useIncidentObjectives(INCIDENT, (objectives) =>
    objectives.map((o) => ({ value: o.objective_id, label: o.name }))
  ).data;
  const { data: resourceSets } = useResourceSets();
  const resourceOptions = useMemo<ConfigSelectOption[]>(
    () =>
      (Array.isArray(resourceSets) ? resourceSets : []).map((r) => ({
        value: r.resource_set_id,
        label: r.name,
      })),
    [resourceSets]
  );
  // Members by NAME, never GUID. Two option sets (PRAT-5710):
  // - 'poc': the incident roster only, matching the UI's POC pickers (e.g. the
  //   action drawers) so a reviewer can only approve a POC they could also set
  //   in the UI.
  // - 'member': union of the incident roster and the whole group roster,
  //   deduped by guid with the incident entry winning — for tools that may
  //   legitimately reference users not yet on the incident (e.g. reviewing the
  //   very assign_roster_position write that adds them).
  const groupGuid = useSelector(selectGroupGuid);
  const { data: incidentRoster } = useIncidentRoster();
  const { data: groupRoster } = useGetRostersForGroup(groupGuid ?? '');
  const pocOptions = useMemo<ConfigSelectOption[]>(
    () =>
      (Array.isArray(incidentRoster) ? incidentRoster : [])
        .map((m) => ({
          value: m.userId,
          label: m.userName || m.emailAddress || m.userId,
        }))
        .sort((a, b) => a.label.localeCompare(b.label)),
    [incidentRoster]
  );
  const memberOptions = useMemo<ConfigSelectOption[]>(() => {
    const byGuid = new Map<string, ConfigSelectOption>();
    for (const m of Array.isArray(groupRoster) ? groupRoster : []) {
      byGuid.set(m.user_guid, {
        value: m.user_guid,
        label: m.user_name || m.email_address || m.user_guid,
      });
    }
    for (const m of Array.isArray(incidentRoster) ? incidentRoster : []) {
      byGuid.set(m.userId, {
        value: m.userId,
        label: m.userName || m.emailAddress || m.userId,
      });
    }
    return [...byGuid.values()].sort((a, b) => a.label.localeCompare(b.label));
  }, [incidentRoster, groupRoster]);

  const optionSets = useMemo<Record<string, ConfigSelectOption[]>>(
    () => ({
      aor: aorOptions,
      objective: objectiveOptions ?? [],
      resource: resourceOptions,
      poc: pocOptions,
      member: memberOptions,
    }),
    [aorOptions, objectiveOptions, resourceOptions, pocOptions, memberOptions]
  );

  const isBatch = state.units.length > 1;
  const respondActive = hasText(currentState?.respondText);
  const verdict = currentState?.verdict;
  const currentStatus = itemStatus(currentState);
  const hasEdits = !!(
    currentState?.edits && Object.keys(currentState.edits).length > 0
  );
  const primaryLabel = respondActive
    ? 'Send response'
    : hasEdits
      ? 'Accept edits'
      : 'Accept';

  // Honor the allowed_decisions contract per unit: the confirmation's list
  // (empty/missing = allow all), minus reject for required-on-create fields.
  const allowed =
    currentItem && currentUnit
      ? unitAllowedDecisions(currentItem, currentUnit)
      : undefined;
  const allows = (d: 'approve' | 'edit' | 'reject'): boolean =>
    !allowed || allowed.includes(d);
  const canReject = allows('reject');
  const primaryAllowed = respondActive
    ? canReject
    : hasEdits
      ? allows('edit')
      : allows('approve');

  // Keyboard 1 = primary (accept), 2 = reject. Suppressed while a field has
  // focus so typing doesn't fire a verdict, and gated on allowed_decisions.
  useEffect(() => {
    if (!show || state.phase !== 'review') return undefined;
    const onKey = (e: KeyboardEvent) => {
      const tag = (document.activeElement?.tagName ?? '').toUpperCase();
      if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT') return;
      if (e.key === '1' && primaryAllowed) queue.setVerdict('accepted');
      else if (e.key === '2' && !respondActive && canReject)
        queue.setVerdict('rejected');
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    show,
    state.phase,
    state.index,
    respondActive,
    primaryAllowed,
    canReject,
  ]);

  const title = 'PRATUS AI needs your approval';

  const handleSubmit = async () => {
    // Arm the reseed for the next batch BEFORE submitting: the batch that
    // comes back after a resume is a new review cycle even when its content
    // is identical to this one (e.g. the agent re-proposes unchanged).
    setAwaitingNextBatch(true);
    queue.submit();
    try {
      const ok = await onSubmit(queue.buildDecisions(optionSets));
      if (ok === false) {
        setAwaitingNextBatch(false);
        queue.revertSubmit();
      }
    } catch {
      setAwaitingNextBatch(false);
      queue.revertSubmit();
    }
  };

  return (
    <Modal
      show={show}
      centered
      backdrop="static"
      keyboard={false}
      size="lg"
      contentClassName="tw:flex tw:max-h-[88vh] tw:flex-col tw:overflow-hidden"
    >
      <Modal.Header className="tw:flex tw:shrink-0 tw:items-center tw:justify-between">
        <Modal.Title>{title}</Modal.Title>
        {onClose && (
          <button
            type="button"
            className="tw:flex tw:cursor-pointer tw:items-center tw:rounded-md tw:border-none tw:bg-transparent tw:p-1 tw:text-[color:var(--text-weak)] tw:hover:bg-[color:var(--background-light)] tw:hover:text-[color:var(--text-white)]"
            onClick={onClose}
            aria-label="Close and return to chat menu"
            title="Close — you can return to this review later"
          >
            <SharedIcon iconName="close" />
          </button>
        )}
      </Modal.Header>

      <Modal.Body className="tw:flex tw:min-h-0 tw:flex-1 tw:flex-col tw:overflow-hidden">
        {askText && (
          <div className="tw:mb-3 tw:line-clamp-2 tw:shrink-0 tw:whitespace-pre-wrap tw:rounded-lg tw:border tw:border-[color:var(--border-primary)] tw:bg-[color:var(--background-light)] tw:px-3 tw:py-2.5 tw:text-[13px] tw:text-[color:var(--text-white)]">
            {askText}
          </div>
        )}

        {state.phase === 'applying' ? (
          <div className="tw:flex tw:min-h-[180px] tw:flex-1 tw:flex-col tw:items-center tw:justify-center tw:gap-3.5 tw:text-center tw:text-[color:var(--text-weak)]">
            <span className="spinner-border" role="status" aria-hidden="true" />
            <p>Writing approved changes &amp; revising your responses…</p>
          </div>
        ) : (
          currentItem &&
          currentUnit && (
            <div className="tw:flex tw:min-h-0 tw:flex-1 tw:gap-4 tw:overflow-hidden">
              {isBatch && (
                <BatchNavigator
                  items={state.items}
                  units={state.units}
                  decisions={state.decisions}
                  activeIndex={state.index}
                  decidedCount={decidedCount}
                  onSelect={queue.goto}
                />
              )}
              <div className="tw:min-h-0 tw:min-w-0 tw:flex-1 tw:overflow-y-auto">
                <ChangeReview
                  item={currentItem}
                  unit={currentUnit}
                  fieldOrdinal={
                    currentUnit.field !== undefined
                      ? state.units
                          .filter(
                            (u) =>
                              u.confirmationIndex ===
                              currentUnit.confirmationIndex
                          )
                          .indexOf(currentUnit) + 1
                      : undefined
                  }
                  fieldCount={
                    currentUnit.field !== undefined
                      ? state.units.filter(
                          (u) =>
                            u.confirmationIndex ===
                            currentUnit.confirmationIndex
                        ).length
                      : undefined
                  }
                  index={state.index}
                  total={state.units.length}
                  cycle={state.cycle}
                  state={currentState}
                  optionSets={optionSets}
                  canRespond={canReject}
                  onEdit={queue.setEdit}
                  onRespond={queue.setRespondText}
                />
              </div>
            </div>
          )
        )}
      </Modal.Body>

      {state.phase === 'review' && (
        <Modal.Footer className="tw:flex tw:shrink-0 tw:flex-nowrap tw:items-center tw:justify-between">
          <div className="tw:flex tw:items-center tw:gap-2.5">
            {/* Accept/Reject are TOGGLES with a filled selected state; neither
                is primary so Submit reads as the single next step once all
                items are decided. Clicking the selected verdict clears it. */}
            <StylishNewButton
              className={
                verdict === 'rejected' ? 'button--error' : 'button--secondary'
              }
              aria-pressed={verdict === 'rejected'}
              onClick={() => queue.setVerdict('rejected')}
              disabled={respondActive || !canReject}
            >
              {verdict === 'rejected' && (
                <SharedIcon
                  iconName="cancel"
                  size="sm"
                  classes="tw:mr-1 tw:!leading-5"
                />
              )}
              Reject
            </StylishNewButton>
            <StylishNewButton
              className={
                verdict === 'accepted' ? 'button--success' : 'button--secondary'
              }
              aria-pressed={verdict === 'accepted'}
              onClick={() => queue.setVerdict('accepted')}
              disabled={!primaryAllowed}
            >
              {verdict === 'accepted' && (
                <SharedIcon
                  iconName="check_circle"
                  size="sm"
                  classes="tw:mr-1 tw:!leading-5"
                />
              )}
              {primaryLabel}
            </StylishNewButton>
          </div>
          {/* Both status variants are single-line with pinned icon metrics so
              the footer keeps a constant height whether (and however) the
              change is decided — wrapping here made the modal grow on decide,
              and by different amounts for accept vs reject. Truncate rather
              than wrap when cramped. */}
          <div className="tw:flex tw:min-w-0 tw:items-center tw:gap-2.5">
            {!isBatch && currentStatus !== 'pending' ? (
              // Single-item batches have no navigator rail, so surface the
              // decision status here instead of an opaque "1 of 1 decided".
              <span className="tw:flex tw:h-5 tw:min-w-0 tw:items-center tw:gap-1 tw:overflow-hidden tw:whitespace-nowrap tw:text-[12px] tw:text-[color:var(--text-weak)]">
                <SharedIcon
                  iconName={STATUS_META[currentStatus].icon}
                  size="sm"
                  color={STATUS_META[currentStatus].color}
                  classes="tw:!leading-5"
                />
                <span className="tw:overflow-hidden tw:text-ellipsis tw:whitespace-nowrap">
                  {STATUS_META[currentStatus].label} — ready to submit
                </span>
              </span>
            ) : (
              <span className="tw:flex tw:h-5 tw:items-center tw:whitespace-nowrap tw:text-[12px] tw:text-[color:var(--text-weak)]">
                {decidedCount} of {state.units.length} decided
              </span>
            )}
            <StylishNewButton
              className="button--primary"
              onClick={handleSubmit}
              disabled={!allDecided || submitting}
            >
              {submitting ? 'Submitting…' : 'Submit'}
            </StylishNewButton>
          </div>
        </Modal.Footer>
      )}
    </Modal>
  );
};

export default ReviewQueueModal;
